import { FC, useContext, useEffect, useMemo, useState } from 'react';
import styles from './CreateFamilyAccountLinkUser.module.scss';
import common from '../../util/scss/common.module.scss';
import BackCardLayout from '../../layout/BackCardLayout/BackCardLayout';
import CreateFamilyAccountContext from '../../contexts/CreateFamilyAccountContext';
import GlobalContext from '../../contexts/GlobalContext';
import { Button, Icon, useTheme } from '@mui/material';
import { useHistory } from 'react-router';
import { EFamilyAccountType, maxFamilyItems } from '../../util/constants';
import { replacePhrase } from '../../util/helpers';
import { IconQuestionCircle } from '../../icons/Icons';
import API from '../../api/api';
import RadioButton from '../RadioButton/RadioButton';
import CoBranding from '../CoBranding/CoBranding';

const CreateFamilyAccountLinkUser: FC = () => {
    const { language, coBranding } = useContext(GlobalContext);
    const theme = useTheme();
    const {
        familyAccountType,
        originalUser,
        primaryUser,
        familyItems,
        selectLinkedMigrationUser,
        selectLinkedConfirmationUser,
        confirmLinkedUser,
        linkedMigrationFamily,
        linkedConfirmationFamily,
    } = useContext(CreateFamilyAccountContext);
    const history = useHistory();
    const [userIsLinked, setUserIsLinked] = useState(false);
    const formattedDesc = useMemo(() => {
        if (familyAccountType === EFamilyAccountType.confirmAccount) {
            return replacePhrase(
                language.createFamilyAccount.manualLink.confirmAccountDesc,
                '<name>',
                originalUser.firstName,
            );
        }
        const string1 = replacePhrase(language.createFamilyAccount.manualLink.desc, '<name>', originalUser.firstName);
        const string2 = replacePhrase(string1, '<name>', originalUser.firstName);
        return string2;
    }, [originalUser.firstName]);
    const formattedAddMissingUser = useMemo(
        () => replacePhrase(language.createFamilyAccount.manualLink.addUser, '<name>', originalUser.firstName),
        [originalUser.firstName],
    );

    const handleGoBack = () => {
        history.goBack();
    };

    const handleSelectUser = (index: number) => {
        if (familyAccountType === EFamilyAccountType.confirmAccount) {
            selectLinkedConfirmationUser(index);
        }
        if (!userIsLinked) {
            setUserIsLinked(true);
        }
    };

    const checkPrimaryUserIsActive = () => {
        if (familyAccountType === EFamilyAccountType.confirmAccount) {
            const isActive = linkedConfirmationFamily.unconfirmedLinkId;
            return isActive ? true : undefined;
        }
        return undefined;
    };

    const checkDependentUserIsActive = (index: number) => {
        if (familyAccountType === EFamilyAccountType.confirmAccount) {
            const isActive =
                linkedConfirmationFamily.dependentUsers[index] &&
                linkedConfirmationFamily.dependentUsers[index].unconfirmedLinkId;
            return isActive ? true : undefined;
        }
        return undefined;
    };

    const handleConfirm = () => {
        API.post.trackEvent({ event: 'Sign In || Migration | Link conflict | Button - Next' });
        confirmLinkedUser();
    };

    // reset linked user in case existing linked user got deleted or changed
    useEffect(() => {
        selectLinkedMigrationUser(null);
        selectLinkedConfirmationUser(null);
    }, []);

    return (
        <BackCardLayout>
            {(coBranding.logoUrl || coBranding.name) && (
                <CoBranding logoUrl={coBranding.logoUrl} name={coBranding.name} />
            )}
            <section className={common.icon} data-testid={'icon'}>
                <IconQuestionCircle strokeColor={theme.palette.grey[900]} />
            </section>
            <header>
                <h2 className={common.pageSubhead} data-testid={'desc'}>
                    {formattedDesc}
                </h2>
            </header>
            <main>
                <section className={styles.usersBlock} data-testid="primary-user">
                    <h5>{language.createFamilyAccount.manualLink.primaryUserTitle}</h5>
                    <RadioButton
                        label={`${primaryUser.firstName} ${primaryUser.lastName}`}
                        isActive={checkPrimaryUserIsActive() ? true : false}
                        onChange={() => {
                            API.post.trackEvent({
                                event: 'Sign In || Migration | Link conflict | Button - Account owner',
                            });
                            handleSelectUser(-1);
                        }}
                        testid="user-picker"
                    />
                </section>
                <section className={styles.usersBlock} data-testid="family-users">
                    <h5>{language.createFamilyAccount.manualLink.familyUsersTitle}</h5>
                    {familyItems.map((item, index) => {
                        const isActive = checkDependentUserIsActive(index) ? true : false;
                        return (
                            <RadioButton
                                key={`${item.firstName}-${item.lastName}-${index}`}
                                label={`${item.firstName} ${item.lastName}`}
                                isActive={isActive}
                                onChange={() => {
                                    API.post.trackEvent({
                                        event: 'Sign In || Migration | Link conflict | Button - Family member',
                                    });
                                    handleSelectUser(index);
                                }}
                                testid="user-picker"
                            />
                        );
                    })}
                </section>
                <section className={styles.editFamily}>
                    <Button onClick={handleGoBack} variant="text" startIcon={<Icon>add_circle</Icon>}>
                        <div tabIndex={-1}>
                            {familyItems.length < maxFamilyItems
                                ? formattedAddMissingUser
                                : language.createFamilyAccount.editFamilyCta}
                        </div>
                    </Button>
                </section>
            </main>
            <footer className={styles.footer}>
                <Button variant="contained" type="button" disabled={!userIsLinked} onClick={handleConfirm} size="large">
                    {language.common.next}
                </Button>
            </footer>
        </BackCardLayout>
    );
};

export default CreateFamilyAccountLinkUser;
